import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getExerciseVisual } from '../data/exerciseVisuals';
import { getClaudeAdvice } from '../utils/claudeApi';
import './Workout.css';

// Component for adding a set
function SetInputForm({ exerciseId, onAddSet, suggestedWeight, hasWeight }) {
  const [weight, setWeight] = useState(suggestedWeight || '');
  const [reps, setReps] = useState('');

  const handleAddSet = () => {
    const parsedReps = parseInt(reps);
    const parsedWeight = hasWeight ? parseFloat(weight) : 0;

    // Validate reps
    if (!parsedReps || parsedReps <= 0 || parsedReps > 1000) {
      alert('Please enter a valid number of reps (1-1000)');
      return;
    }

    // Validate weight if exercise requires it
    if (hasWeight && (!parsedWeight || parsedWeight < 0 || parsedWeight > 10000)) {
      alert('Please enter a valid weight (0-10000 lbs)');
      return;
    }

    onAddSet(exerciseId, parsedWeight, parsedReps);
    setReps('');
    // Keep weight for next set
  };

  return (
    <div className="set-input-form">
      {hasWeight && (
        <input
          type="number"
          placeholder="Weight (lbs)"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className="weight-input"
        />
      )}
      <input
        type="number"
        placeholder="Reps"
        value={reps}
        onChange={(e) => setReps(e.target.value)}
        className="reps-input"
      />
      <button onClick={handleAddSet} className="add-set-btn">
        + Add Set
      </button>
    </div>
  );
}

// Helper function to determine if an exercise requires weight input
function isWeightedExercise(exercise) {
  // If exercise has a machine property, it requires weight
  if (exercise.machine !== undefined) return true;

  const exerciseName = exercise.name.toLowerCase();

  // Bodyweight-only exercise keywords
  const bodyweightKeywords = [
    'push-up', 'pushup', 'pull-up', 'pullup', 'chin-up', 'chinup',
    'plank', 'jumping', 'air squat', 'bodyweight squat',
    'mountain climber', 'burpee', 'crunch', 'sit-up', 'situp',
    'leg raise', 'hanging leg raise', 'flutter kick', 'bicycle',
    'russian twist'
  ];

  // Check if it's explicitly a bodyweight exercise
  if (bodyweightKeywords.some(keyword => exerciseName.includes(keyword))) {
    return false;
  }

  // Weighted exercise keywords
  const weightedKeywords = [
    'barbell', 'dumbbell', 'kettlebell', 'cable', 'machine',
    'press', 'row', 'curl', 'extension', 'fly', 'raise',
    'squat', 'deadlift', 'lunge', 'bench', 'overhead',
    'lat pulldown', 'leg press', 'leg curl', 'leg extension',
    'chest press', 'shoulder press', 'tricep', 'bicep'
  ];

  // Check if it contains weighted exercise keywords
  if (weightedKeywords.some(keyword => exerciseName.includes(keyword))) {
    return true;
  }

  // Default to no weight for ambiguous exercises
  return false;
}

function Workout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workout, day } = location.state || {};
  const [completedExercises, setCompletedExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exerciseData, setExerciseData] = useState({});
  const [previousWorkout, setPreviousWorkout] = useState(null);
  const [expandedInfo, setExpandedInfo] = useState({});
  const [claudeAdvice, setClaudeAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [adviceError, setAdviceError] = useState(null);
  const [originalWorkout, setOriginalWorkout] = useState(null);

  useEffect(() => {
    // Load previous workout data
    if (workout && day) {
      // Store original workout for switching
      if (!originalWorkout) {
        setOriginalWorkout(workout);
      }

      const detailedHistory = localStorage.getItem('detailedWorkoutHistory');
      if (detailedHistory) {
        const history = JSON.parse(detailedHistory);
        // Find the most recent workout for this day
        const previous = history
          .filter(w => w.day === day)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        setPreviousWorkout(previous);

        // Initialize exerciseData with previous values or defaults
        const initialData = {};
        workout.exercises.forEach(ex => {
          const prevEx = previous?.exercises?.find(e => e.id === ex.id);
          initialData[ex.id] = {
            sets: prevEx?.sets || [],
            difficulty: prevEx?.difficulty || 5,
            notes: prevEx?.notes || ''
          };
        });
        setExerciseData(initialData);
      } else {
        // Initialize empty data
        const initialData = {};
        workout.exercises.forEach(ex => {
          initialData[ex.id] = {
            sets: [],
            difficulty: 5,
            notes: ''
          };
        });
        setExerciseData(initialData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout, day]);

  if (!workout) {
    return (
      <div className="container">
        <p>No workout selected</p>
        <button onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const addSet = (exerciseId, weight, reps) => {
    setExerciseData(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: [...(prev[exerciseId]?.sets || []), { weight, reps }]
      }
    }));
  };

  const removeSet = (exerciseId, setIndex) => {
    setExerciseData(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        sets: prev[exerciseId].sets.filter((_, i) => i !== setIndex)
      }
    }));
  };

  const updateDifficulty = (exerciseId, difficulty) => {
    setExerciseData(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        difficulty
      }
    }));
  };

  const toggleExerciseComplete = (exerciseId) => {
    if (completedExercises.includes(exerciseId)) {
      setCompletedExercises(completedExercises.filter(id => id !== exerciseId));
    } else {
      setCompletedExercises([...completedExercises, exerciseId]);
    }
  };

  const toggleInfoExpanded = (exerciseId) => {
    setExpandedInfo(prev => ({
      ...prev,
      [exerciseId]: !prev[exerciseId]
    }));
  };

  const handleSwitchToHome = () => {
    // Navigate back to home page with home alternative workout
    if (!originalWorkout?.homeAlternative?.name ||
        !originalWorkout?.homeAlternative?.exercises) {
      alert('No home alternative available for this workout.');
      return;
    }

    // Create a new workout object using the home alternative
    const homeWorkout = {
      ...originalWorkout,
      name: originalWorkout.homeAlternative.name,
      location: 'Home',
      exercises: originalWorkout.homeAlternative.exercises
    };

    // Navigate to workout page with home alternative
    navigate('/workout', {
      state: {
        workout: homeWorkout,
        day: day
      },
      replace: true
    });
  };

  const handleSwitchToGym = () => {
    // Navigate back to original gym workout
    if (!originalWorkout) return;

    navigate('/workout', {
      state: {
        workout: originalWorkout,
        day: day
      },
      replace: true
    });
  };

  const handleGetAdvice = async () => {
    setLoadingAdvice(true);
    setAdviceError(null);
    setClaudeAdvice(null);

    try {
      // Get workout history
      const detailedHistory = localStorage.getItem('detailedWorkoutHistory');
      const workoutHistory = detailedHistory ? JSON.parse(detailedHistory) : [];

      // Get advice from Claude
      const advice = await getClaudeAdvice(workoutHistory, workout, {});
      setClaudeAdvice(advice);
    } catch (error) {
      setAdviceError(error.message);
    } finally {
      setLoadingAdvice(false);
    }
  };

  const completeWorkout = () => {
    // Save detailed workout data
    const detailedWorkoutData = {
      day,
      date: new Date().toISOString(),
      workoutName: workout.name,
      exercises: workout.exercises.map(ex => ({
        id: ex.id,
        name: ex.name,
        sets: exerciseData[ex.id]?.sets || [],
        difficulty: exerciseData[ex.id]?.difficulty || 5,
        notes: exerciseData[ex.id]?.notes || '',
        completed: completedExercises.includes(ex.id)
      }))
    };

    // Save simple workout history (for backward compatibility)
    const simpleWorkoutData = {
      day,
      date: new Date().toISOString(),
      workoutName: workout.name,
      completedExercises: completedExercises.length,
      totalExercises: workout.exercises.length,
    };

    try {
      // Save simple history
      const existingData = localStorage.getItem('workoutHistory');
      const history = existingData ? JSON.parse(existingData) : [];
      history.push(simpleWorkoutData);
      localStorage.setItem('workoutHistory', JSON.stringify(history));

      // Save detailed history
      const detailedHistory = localStorage.getItem('detailedWorkoutHistory');
      const detailedHistoryArray = detailedHistory ? JSON.parse(detailedHistory) : [];
      detailedHistoryArray.push(detailedWorkoutData);
      localStorage.setItem('detailedWorkoutHistory', JSON.stringify(detailedHistoryArray));

      // Provide detailed feedback
      const setsLogged = workout.exercises.reduce((count, ex) => {
        return count + (exerciseData[ex.id]?.sets?.length || 0);
      }, 0);

      let message = `✅ Workout Saved!\n\n`;
      message += `📊 Summary:\n`;
      message += `• ${completedExercises.length} of ${workout.exercises.length} exercises completed\n`;
      message += `• ${setsLogged} total sets logged\n`;

      if (completedExercises.length === workout.exercises.length) {
        message += `\n🎉 Perfect! You completed all exercises!`;
      } else if (completedExercises.length > 0) {
        message += `\nGreat effort! Your progress has been recorded.`;
      } else {
        message += `\nWorkout data saved. Remember to mark exercises as complete next time!`;
      }

      alert(message);
      navigate('/');
    } catch (error) {
      console.error('Error saving workout:', error);
    }
  };

  const getSuggestedWeight = (exerciseId, prevExercise) => {
    if (!prevExercise || !prevExercise.sets || prevExercise.sets.length === 0) return null;

    const sets = prevExercise.sets;
    const weights = sets.map(s => parseFloat(s.weight) || 0);
    const reps = sets.map(s => parseInt(s.reps) || 0);

    // Check if bodyweight exercise
    if (weights.every(w => w === 0)) return null;

    const avgWeight = weights.reduce((a, b) => a + b, 0) / weights.length;
    const maxWeight = Math.max(...weights);

    // Analyze set-by-set consistency
    const firstSetReps = reps[0];
    const lastSetReps = reps[reps.length - 1];
    const repDropoff = sets.length >= 2 ? ((firstSetReps - lastSetReps) / firstSetReps * 100) : 0;

    // Check if weight was maintained across sets
    const weightConsistent = weights.every(w => w === maxWeight);

    // Determine weight category for context-aware recommendations
    const isLightWeight = avgWeight < 40;
    const isMediumWeight = avgWeight >= 40 && avgWeight < 100;
    const isHeavyWeight = avgWeight >= 100;

    let suggestedIncrease = 0;

    // Pattern 1: Consistent performance across sets (e.g., 20×10, 20×10, 20×9)
    if (weightConsistent && repDropoff < 15 && prevExercise.difficulty >= 6 && prevExercise.difficulty <= 8) {
      // User maintained weight well - ready for progressive overload
      if (isLightWeight) suggestedIncrease = 5;
      else if (isMediumWeight) suggestedIncrease = 5;
      else if (isHeavyWeight) suggestedIncrease = 10;
    }
    // Pattern 2: Significant drop-off (>30% rep decrease)
    else if (repDropoff > 30 || !weightConsistent) {
      // Starting weight too heavy OR rest periods too short
      if (prevExercise.difficulty >= 8) {
        // Too heavy - reduce weight
        if (isLightWeight) suggestedIncrease = -2.5;
        else if (isMediumWeight) suggestedIncrease = -5;
        else if (isHeavyWeight) suggestedIncrease = -10;
      } else {
        // Maintain current weight, improve consistency
        suggestedIncrease = 0;
      }
    }
    // Pattern 3: Progressive fatigue (e.g., 20×12, 20×10, 20×8) - normal
    else if (repDropoff >= 15 && repDropoff <= 30 && weightConsistent) {
      // Normal fatigue pattern
      if (prevExercise.difficulty >= 6 && prevExercise.difficulty <= 7) {
        // Can increase slightly
        if (isLightWeight) suggestedIncrease = 2.5;
        else if (isMediumWeight) suggestedIncrease = 5;
        else if (isHeavyWeight) suggestedIncrease = 5;
      } else if (prevExercise.difficulty >= 8) {
        // Maintain or increase reps instead
        suggestedIncrease = 0;
      } else {
        // Too easy
        if (isLightWeight) suggestedIncrease = 5;
        else if (isMediumWeight) suggestedIncrease = 10;
        else if (isHeavyWeight) suggestedIncrease = 10;
      }
    }
    // Pattern 4: Very high difficulty (9-10) but completed sets
    else if (prevExercise.difficulty >= 9) {
      // Too difficult - maintain or reduce slightly
      if (repDropoff > 20) {
        suggestedIncrease = -5;
      } else {
        suggestedIncrease = 0;
      }
    }
    // Pattern 5: Too easy (difficulty 1-4)
    else if (prevExercise.difficulty <= 4) {
      // Add significant weight
      if (isLightWeight) suggestedIncrease = 5;
      else if (isMediumWeight) suggestedIncrease = 10;
      else if (isHeavyWeight) suggestedIncrease = 15;
    }
    // Default: Moderate difficulty (5-6)
    else {
      suggestedIncrease = 2.5;
    }

    const suggested = Math.max(0, Math.round((avgWeight + suggestedIncrease) * 2) / 2); // Round to nearest 2.5 lbs
    return suggested > 0 ? suggested : null;
  };

  return (
    <div className="container">
      <div className="workout-screen">
        <div className="workout-nav">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back
          </button>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${(completedExercises.length / workout.exercises.length) * 100}%` }}
            />
          </div>
          <p className="progress-text">
            {completedExercises.length} / {workout.exercises.length} completed
          </p>
        </div>

        <div className="workout-info">
          <h2 className="workout-name">{workout.name}</h2>
          <p className="workout-meta-line">
            {workout.duration} • {workout.location}
          </p>
          {workout.locationNote && (
            <p className="workout-location-note">📍 {workout.locationNote}</p>
          )}
          <p className="workout-focus">Focus: {workout.focus}</p>

          {/* Switch Workout Option (during workout) */}
          <div className="workout-options">
            {workout.location && (workout.location.toLowerCase().includes('gym') || workout.location.toLowerCase().includes('planet')) && originalWorkout?.homeAlternative && (
              <button
                className="home-alternative-btn"
                onClick={handleSwitchToHome}
              >
                🏠 Switch to Home Workout
              </button>
            )}
            {workout.location && workout.location.toLowerCase() === 'home' && originalWorkout && originalWorkout.location !== 'Home' && (
              <button
                className="home-alternative-btn"
                onClick={handleSwitchToGym}
              >
                🏋️ Switch to Gym Workout
              </button>
            )}
          </div>
        </div>

        {/* Claude AI Advice Section */}
        <div className="claude-advice-section">
          <button
            className="claude-advice-button"
            onClick={handleGetAdvice}
            disabled={loadingAdvice}
          >
            {loadingAdvice ? '🤔 Thinking...' : '🤖 Ask Claude for Workout Advice'}
          </button>

          {adviceError && (
            <div className="advice-error">
              <p>⚠️ {adviceError}</p>
              {adviceError.includes('API key') && (
                <button
                  className="settings-link-button"
                  onClick={() => navigate('/settings')}
                >
                  Go to Settings
                </button>
              )}
            </div>
          )}

          {claudeAdvice && (
            <div className="advice-content">
              <div className="advice-header">
                <span className="advice-icon">💡</span>
                <span className="advice-title">Claude's Advice</span>
              </div>
              <div className="advice-text">
                {claudeAdvice.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Warm-up Section */}
        {workout.warmUp && workout.warmUp.length > 0 && (
          <div className="warmup-section">
            <h3 className="section-title">🔥 Warm-Up (5-7 min)</h3>
            <p className="section-note">Prepare your body, increase blood flow, prevent injury</p>
            <div className="warmup-list">
              {workout.warmUp.map((warmupEx, idx) => (
                <div key={idx} className="warmup-item">
                  <div className="warmup-name">{warmupEx.name}</div>
                  <div className="warmup-duration">{warmupEx.duration}</div>
                  {warmupEx.notes && <div className="warmup-notes">{warmupEx.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="exercises-list">
          <h3 className="section-title">💪 Main Workout</h3>
          {workout.exercises.map((exercise, index) => {
            const isCompleted = completedExercises.includes(exercise.id);
            const isCurrent = index === currentExerciseIndex;
            const prevExercise = previousWorkout?.exercises?.find(e => e.id === exercise.id);
            const currentData = exerciseData[exercise.id] || { sets: [], difficulty: 5 };
            const suggestedWeight = getSuggestedWeight(exercise.id, prevExercise);
            const visualInfo = getExerciseVisual(exercise.name);
            const isInfoExpanded = expandedInfo[exercise.id];

            return (
              <div
                key={exercise.id}
                className={`exercise-card ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
              >
                <div className="exercise-header">
                  <div className="exercise-number">{index + 1}</div>
                  <div className="exercise-title-container">
                    <h4 className={`exercise-name ${isCompleted ? 'strike' : ''}`}>
                      {exercise.name}
                    </h4>
                    <p className="exercise-details">
                      {exercise.sets} sets × {exercise.reps} reps • Rest: {exercise.rest}
                    </p>
                  </div>
                </div>

                {/* Visual Guide Button - PROMINENT AT TOP */}
                {(visualInfo || exercise.howTo) && (
                  <button
                    className="visual-guide-toggle-prominent"
                    onClick={() => toggleInfoExpanded(exercise.id)}
                  >
                    <span className="guide-icon">{isInfoExpanded ? '📖 Hide' : '📖 View'}</span>
                    <span className="guide-text">Exercise Guide & Form</span>
                    <span className="guide-arrow">{isInfoExpanded ? '▼' : '▶'}</span>
                  </button>
                )}

                {/* Expanded Visual Guide Content */}
                {isInfoExpanded && (
                  <div className="visual-guide-content-prominent">
                    {visualInfo ? (
                      <>
                        {/* Machine Type */}
                        <div className="visual-info-block">
                          <h5 className="visual-info-title">Machine/Equipment:</h5>
                          <p className="visual-info-text">{visualInfo.machineType}</p>
                        </div>

                        {/* How to Find */}
                        <div className="visual-info-block">
                          <h5 className="visual-info-title">🔍 How to Find It:</h5>
                          <p className="visual-info-text">{visualInfo.howToFind}</p>
                        </div>

                        {/* Visual Description */}
                        {visualInfo.visualDescription && (
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">👁️ What It Looks Like:</h5>
                            <p className="visual-info-text">{visualInfo.visualDescription}</p>
                          </div>
                        )}

                        {/* Setup Instructions */}
                        <div className="visual-info-block">
                          <h5 className="visual-info-title">⚙️ How to Set Up & Perform:</h5>
                          <ol className="setup-list">
                            {visualInfo.setup.map((step, i) => (
                              <li key={i}>{step}</li>
                            ))}
                          </ol>
                        </div>

                        {/* Target Muscles */}
                        <div className="visual-info-block">
                          <h5 className="visual-info-title">🎯 Muscles Worked:</h5>
                          <div className="muscle-tags">
                            {visualInfo.targetMuscles.map((muscle, i) => (
                              <span key={i} className="muscle-tag">{muscle}</span>
                            ))}
                          </div>
                        </div>

                        {/* Common Mistakes */}
                        {visualInfo.commonMistakes && (
                          <div className="visual-info-block mistakes-block">
                            <h5 className="visual-info-title">⚠️ Common Mistakes to Avoid:</h5>
                            <ul className="mistakes-list">
                              {visualInfo.commonMistakes.map((mistake, i) => (
                                <li key={i}>{mistake}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Variations */}
                        {visualInfo.variations && (
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">🔄 Variations:</h5>
                            <ul className="variations-list">
                              {visualInfo.variations.map((variation, i) => (
                                <li key={i}>{variation}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Video Tutorial Link */}
                        {visualInfo.videoTutorial && (
                          <div className="visual-info-block video-block">
                            <h5 className="visual-info-title">📹 Video Tutorial:</h5>
                            <p className="video-tutorial-text">{visualInfo.videoTutorial}</p>
                          </div>
                        )}
                      </>
                    ) : exercise.howTo ? (
                      <>
                        {/* AI-Generated Exercise Instructions */}
                        <div className="visual-info-block">
                          <h5 className="visual-info-title">⚙️ How to Perform This Exercise:</h5>
                          <p className="visual-info-text" style={{whiteSpace: 'pre-line'}}>{exercise.howTo}</p>
                        </div>
                        {exercise.notes && (
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">💡 Tips & Notes:</h5>
                            <p className="visual-info-text">{exercise.notes}</p>
                          </div>
                        )}
                        {exercise.progressionTips && (
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">📈 Progression Tips:</h5>
                            <p className="visual-info-text">{exercise.progressionTips}</p>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                )}

                <div className="exercise-body">
                  {/* Only show notes here if not already displayed in visual guide */}
                  {!visualInfo && !exercise.howTo && exercise.notes && (
                    <p className="exercise-notes">{exercise.notes}</p>
                  )}

                  {/* Visual Guide Toggle */}
                  {visualInfo && false && (
                    <div className="visual-guide-section">
                      <button
                        className="visual-guide-toggle"
                        onClick={() => toggleInfoExpanded(exercise.id)}
                      >
                        {isInfoExpanded ? '▼' : '▶'} How to do this exercise (with visuals)
                      </button>

                      {isInfoExpanded && (
                        <div className="visual-guide-content">
                          {/* Machine Type */}
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">Machine/Equipment:</h5>
                            <p className="visual-info-text">{visualInfo.machineType}</p>
                          </div>

                          {/* How to Find */}
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">🔍 How to Find It:</h5>
                            <p className="visual-info-text">{visualInfo.howToFind}</p>
                          </div>

                          {/* Visual Description */}
                          {visualInfo.visualDescription && (
                            <div className="visual-info-block">
                              <h5 className="visual-info-title">👁️ What It Looks Like:</h5>
                              <p className="visual-info-text">{visualInfo.visualDescription}</p>
                            </div>
                          )}

                          {/* Setup Instructions */}
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">⚙️ How to Set Up & Perform:</h5>
                            <ol className="setup-list">
                              {visualInfo.setup.map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ol>
                          </div>

                          {/* Target Muscles */}
                          <div className="visual-info-block">
                            <h5 className="visual-info-title">🎯 Muscles Worked:</h5>
                            <div className="muscle-tags">
                              {visualInfo.targetMuscles.map((muscle, i) => (
                                <span key={i} className="muscle-tag">{muscle}</span>
                              ))}
                            </div>
                          </div>

                          {/* Common Mistakes */}
                          {visualInfo.commonMistakes && (
                            <div className="visual-info-block mistakes-block">
                              <h5 className="visual-info-title">⚠️ Common Mistakes to Avoid:</h5>
                              <ul className="mistakes-list">
                                {visualInfo.commonMistakes.map((mistake, i) => (
                                  <li key={i}>{mistake}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Variations */}
                          {visualInfo.variations && (
                            <div className="visual-info-block">
                              <h5 className="visual-info-title">🔄 Variations:</h5>
                              <ul className="variations-list">
                                {visualInfo.variations.map((variation, i) => (
                                  <li key={i}>{variation}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Video Tutorial Link */}
                          {visualInfo.videoTutorial && (
                            <div className="visual-info-block video-block">
                              <h5 className="visual-info-title">📹 Video Tutorial:</h5>
                              <p className="video-tutorial-text">{visualInfo.videoTutorial}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Previous Workout Stats */}
                  {prevExercise && prevExercise.sets && prevExercise.sets.length > 0 && (
                    <div className="previous-stats">
                      <p className="previous-label">📊 Last Workout:</p>
                      <div className="previous-sets">
                        {prevExercise.sets.map((set, i) => (
                          <span key={i} className="previous-set">
                            {set.weight ? `${set.weight} lbs` : 'BW'} × {set.reps}
                          </span>
                        ))}
                      </div>
                      <p className="previous-difficulty">
                        Difficulty: {prevExercise.difficulty}/10
                        {prevExercise.difficulty >= 7 && ' 💪 (Increase weight!)'}
                        {prevExercise.difficulty <= 4 && ' ⚠️ (Decrease weight)'}
                      </p>
                    </div>
                  )}

                  {/* Suggested Weight */}
                  {suggestedWeight && (
                    <div className="suggestion-box">
                      <p className="suggestion-text">💡 Suggested: {suggestedWeight} lbs</p>
                    </div>
                  )}

                  {/* Log Sets */}
                  <div className="sets-logging">
                    <p className="sets-label">Log Your Sets:</p>
                    {currentData.sets.map((set, setIndex) => (
                      <div key={setIndex} className="set-row">
                        <span className="set-number">Set {setIndex + 1}:</span>
                        <span className="set-data">
                          {set.weight ? `${set.weight} lbs` : 'Bodyweight'} × {set.reps} reps
                        </span>
                        <button
                          className="remove-set-btn"
                          onClick={() => removeSet(exercise.id, setIndex)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <SetInputForm
                      exerciseId={exercise.id}
                      onAddSet={addSet}
                      suggestedWeight={suggestedWeight}
                      hasWeight={isWeightedExercise(exercise)}
                    />
                  </div>

                  {/* Difficulty Slider */}
                  <div className="difficulty-section">
                    <label className="difficulty-label">
                      How hard was this? {currentData.difficulty}/10
                    </label>
                    <div className="difficulty-slider-container">
                      <span className="difficulty-min">Easy 😊</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={currentData.difficulty}
                        onChange={(e) => updateDifficulty(exercise.id, parseInt(e.target.value))}
                        className="difficulty-slider"
                      />
                      <span className="difficulty-max">Hard 🔥</span>
                    </div>
                    <div className="difficulty-guide">
                      {currentData.difficulty >= 8 && <p>💪 Great! Try more weight next time</p>}
                      {currentData.difficulty >= 5 && currentData.difficulty < 8 && <p>👍 Perfect intensity</p>}
                      {currentData.difficulty < 5 && <p>⚠️ Too easy - add weight/reps</p>}
                    </div>
                  </div>

                  {exercise.machine && (
                    <div className="machine-info">
                      <p className="machine-label">🏋️ Machine:</p>
                      <p className="machine-text">{exercise.machine}</p>
                    </div>
                  )}

                  {exercise.progressionTips && (
                    <div className="tips-container">
                      <p className="tips-label">💡 Progression:</p>
                      <p className="tips-text">{exercise.progressionTips}</p>
                    </div>
                  )}
                </div>

                <button
                  className={`complete-button ${isCompleted ? 'done' : ''}`}
                  onClick={() => {
                    toggleExerciseComplete(exercise.id);
                    if (!isCompleted && index < workout.exercises.length - 1) {
                      setCurrentExerciseIndex(index + 1);
                    }
                  }}
                >
                  {isCompleted ? '✓ Completed' : 'Mark Complete'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Cool-down Section */}
        {workout.coolDown && workout.coolDown.length > 0 && (
          <div className="cooldown-section">
            <h3 className="section-title">🧘 Cool-Down & Stretching (5-7 min)</h3>
            <p className="section-note">Hold each stretch 30-60 seconds, breathe deeply, promote recovery</p>
            <div className="cooldown-list">
              {workout.coolDown.map((cooldownEx, idx) => (
                <div key={idx} className="cooldown-item">
                  <div className="cooldown-name">{cooldownEx.name}</div>
                  <div className="cooldown-duration">{cooldownEx.duration}</div>
                  {cooldownEx.notes && <div className="cooldown-notes">{cooldownEx.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cardio Section */}
        {workout.cardio && workout.cardio.type && workout.cardio.type !== 'none' && workout.cardio.exercises && workout.cardio.exercises.length > 0 && (
          <div className="cardio-section">
            <h3 className="section-title">🏃 Cardio / Conditioning ({workout.cardio.duration})</h3>
            <p className="section-note">{workout.cardio.notes}</p>
            <div className="cardio-list">
              {workout.cardio.exercises.map((cardioEx, idx) => (
                <div key={idx} className="cardio-item">
                  <div className="cardio-name">{cardioEx.name}</div>
                  <div className="cardio-details">
                    <span className="cardio-duration">{cardioEx.duration}</span>
                    {cardioEx.intensity && <span className="cardio-intensity"> • {cardioEx.intensity} Intensity</span>}
                  </div>
                  {cardioEx.notes && <div className="cardio-notes">{cardioEx.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Finish Workout Button - Always Visible */}
        <div className="finish-footer">
          {completedExercises.length === workout.exercises.length ? (
            <div className="finish-message-complete">
              <p className="finish-congrats">🎉 All exercises completed! Great work! 🎉</p>
              <button className="finish-button finish-button-complete" onClick={completeWorkout}>
                SAVE & FINISH WORKOUT
              </button>
            </div>
          ) : (
            <div className="finish-message-partial">
              <p className="finish-info">
                {completedExercises.length > 0
                  ? `You've completed ${completedExercises.length} of ${workout.exercises.length} exercises.`
                  : 'Mark exercises as complete as you go, then save your workout.'}
              </p>
              <button className="finish-button finish-button-partial" onClick={completeWorkout}>
                {completedExercises.length > 0 ? 'SAVE & FINISH EARLY' : 'SAVE WORKOUT'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Workout;
